import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, SquarePen, Trash2 } from 'lucide-react';
import { getFaqTypes, createFaqType, updateFaqType, deleteFaqType, getFaqTypeByCategoryId } from '../../Services/ContentHub/FaqsApi';
import DeleteModal from '../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';

const ActionMenu = ({ onEdit, onDelete, onViewFaqs }) => {
    return (
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
            <Tooltip title="View FAQs">
                <IconButton disableFocusRipple disableRipple disableTouchRipple size="small" onClick={onViewFaqs} sx={{ color: '#444', '&:hover': { color: '#FFBD65' } }}>
                    <Eye size={16} />
                </IconButton>
            </Tooltip>
            <PermissionWrapper permission="edit" mode="disable">
                <Tooltip title="Edit">
                    <IconButton disableFocusRipple disableRipple disableTouchRipple size="small" onClick={onEdit} sx={{ color: '#FFBD65' }}>
                        <SquarePen size={16} />
                    </IconButton>
                </Tooltip>
            </PermissionWrapper>
            <PermissionWrapper permission="delete" mode="disable">
                <Tooltip title="Delete">
                    <IconButton disableFocusRipple disableRipple disableTouchRipple size="small" onClick={onDelete} sx={{ color: '#FF4D49' }}>
                        <Trash2 size={16} />
                    </IconButton>
                </Tooltip>
            </PermissionWrapper>
        </Stack>
    );
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const FaqsTypes = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useGlobalSnackbar();
    const [faqTypes, setFaqTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('all');

    // Modal state
    const [openModal, setOpenModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({
        name: '',
        faqsCategories: 'host'
    });
    const [fieldErrors, setFieldErrors] = useState({});

    // Delete modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const categories = [
        { label: 'Host', value: 'host' },
        { label: 'Guest', value: 'guest' }
    ];

    const fetchFaqTypes = useCallback(async () => {
        try {
            setLoading(true);
            let response;
            if (selectedCategory === 'all') {
                response = await getFaqTypes();
            } else {
                response = await getFaqTypeByCategoryId(selectedCategory);
            }

            if (response && response.success) {
                setFaqTypes(response.data);
            } else {
                setError(response?.message || "Failed to load FAQ types.");
                setFaqTypes([]);
            }
        } catch (err) {
            setError("Failed to fetch FAQ types.");
            setFaqTypes([]);
        } finally {
            setLoading(false);
        }
    }, [selectedCategory]);

    useEffect(() => {
        fetchFaqTypes();
    }, [fetchFaqTypes]);

    const handleOpenModal = (type = null) => {
        setFieldErrors({});
        if (type) {
            setForm({
                name: type.name,
                faqsCategories: type.faqsCategories || 'host'
            });
            setEditId(type.id);
        } else {
            setForm({ name: '', faqsCategories: 'host' });
            setEditId(null);
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setFieldErrors({});
        setError(null);
    };

    const handleNameChange = (e) => {
        setForm({ ...form, name: e.target.value });
        if (fieldErrors.name) {
            setFieldErrors(prev => ({ ...prev, name: '' }));
        }
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            setFieldErrors({ name: "Name is required" });
            return;
        }
        setFieldErrors({});

        setSubmitting(true);
        try {
            let response;
            if (editId) {
                response = await updateFaqType(editId, form);
            } else {
                response = await createFaqType(form);
            }

            if (response && response.success) {
                showSnackbar(editId ? "FAQ Type updated successfully!" : "FAQ Type created successfully!", "success");
                handleCloseModal();
                fetchFaqTypes();
            } else {
                showSnackbar(response?.message || "Failed to save FAQ type.", "error");
            }
        } catch (err) {
            console.error(err);
            const serverError = err.response?.data;
            const errMsg = serverError?.errors?.[0] ||
                serverError?.details?.[0]?.message ||
                serverError?.message ||
                err.message ||
                "Failed to save FAQ type.";

            showSnackbar(errMsg, "error");

            const errorsObj = {};
            if (serverError && serverError.details && Array.isArray(serverError.details)) {
                serverError.details.forEach(item => {
                    const fieldName = item.field === 'general' ? 'name' : item.field;
                    errorsObj[fieldName] = item.message;
                });
            } else if (serverError && serverError.errors && Array.isArray(serverError.errors) && serverError.errors.length > 0) {
                errorsObj['name'] = serverError.errors[0];
            } else {
                errorsObj['name'] = errMsg;
            }
            setFieldErrors(errorsObj);
        } finally {
            setSubmitting(false);
        }
    };

    const handleViewFaqs = (id) => {
        navigate('/content-hub/faqs', { state: { faqsTypeId: id } });
    };

    const handleOpenDelete = (id) => {
        setIdToDelete(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!idToDelete) return;

        try {
            setIsDeleting(true);
            setDeleteError(null);
            const response = await deleteFaqType(idToDelete);
            if (response && response.success) {
                showSnackbar("FAQ Type deleted successfully!", "success");
                setDeleteModalOpen(false);
                setIdToDelete(null);
                fetchFaqTypes();
            } else {
                const errMsg = response.message || "Failed to delete FAQ type";
                setDeleteError(errMsg);
                showSnackbar(errMsg, "error");
            }
        } catch (err) {
            console.error("Delete Error:", err);
            const errorMessage = err.response?.data?.message || err.message || "An error occurred while deleting FAQ type";
            setDeleteError(errorMessage);
            showSnackbar(errorMessage, "error");
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                            Faqs Types
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <TextField
                            select
                            size="small"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            sx={{
                                minWidth: 150,
                                bgcolor: '#fff',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    height: '40px',
                                    fontSize: '0.85rem',
                                    '& fieldset': { borderColor: '#E0E0E0' },
                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                }
                            }}
                        >
                            <MenuItem value="all" sx={{ fontSize: '0.85rem' }}>All Categories</MenuItem>
                            {categories.map((cat) => (
                                <MenuItem key={cat.value} value={cat.value} sx={{ fontSize: '0.85rem' }}>{cat.label}</MenuItem>
                            ))}
                        </TextField>
                        <PermissionWrapper permission="create" mode="disable">
                            <Button
                                disableElevation disableRipple disableFocusRipple disableTouchRipple
                                variant="contained" startIcon={<Plus size={18} />}
                                onClick={() => handleOpenModal()}
                                sx={{
                                    bgcolor: '#FFBD65',
                                    px: 2.5,
                                    color: '#fff',
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    height: '40px',
                                    fontSize: '0.85rem'
                                }}
                            >
                                Add FAQ Type
                            </Button>
                        </PermissionWrapper>
                    </Stack>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 950 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Category</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px', textAlign: 'right' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : faqTypes.length > 0 ? (
                                    faqTypes.map((type, idx) => (
                                        <TableRow key={type.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#333', fontWeight: 500 }}>
                                                <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '0.8rem' }}>{type.name}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>
                                                <Typography sx={{ color: '#666', textTransform: 'capitalize', fontSize: '0.8rem' }}>{type.faqsCategories}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {type.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {type.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(type.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(type.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <ActionMenu
                                                    onEdit={() => handleOpenModal(type)}
                                                    onDelete={() => handleOpenDelete(type.id)}
                                                    onViewFaqs={() => handleViewFaqs(type.id)}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No FAQ types found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>{editId ? 'Edit FAQ Type' : 'Add FAQ Type'}</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Name *"
                                placeholder="e.g. Booking Process"
                                value={form.name}
                                onChange={handleNameChange}
                                error={Boolean(fieldErrors.name)}
                                helperText={fieldErrors.name}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Category"
                                value={form.faqsCategories}
                                onChange={(e) => setForm({ ...form, faqsCategories: e.target.value })}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            >
                                {categories.map((option) => (
                                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.85rem' }}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple onClick={handleCloseModal} sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem', px: 2.5 }}>Cancel</Button>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleSubmit}
                            disabled={submitting}
                            variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 3,
                                color: '#fff',
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete FAQ Type"
                    description="Are you sure you want to delete this FAQ type? This action will not delete associated FAQs but the type reference will be removed."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default FaqsTypes;
